export function normalizeDescopedSelections(value: unknown): string[] {
    const sourceValue = resolveDescopedSelectionsSource(value);
    if (!sourceValue) {
        return [];
    }

    const source = normalizeDescopedSelectionsSource(sourceValue);

    const normalized = new Set<string>();
    for (const entry of source) {
        const selection = normalizeDescopedSelectionEntry(entry);
        if (!selection) {
            continue;
        }

        normalized.add(selection);
    }

    return Array.from(normalized);
}

export function formatDescopedSelectionsLabel(value: unknown): string | null {
    const selections = normalizeDescopedSelections(value);
    if (!selections.length) {
        return null;
    }

    const visibleSelections = selections.slice(0, 2).join(" + ");
    const remainingCount = selections.length - 2;
    return `In-Scope SAQs: ${visibleSelections}${remainingCount > 0 ? ` + ${remainingCount} more` : ""}`;
}

function resolveDescopedSelectionsSource(value: unknown): unknown {
    if (!value || Array.isArray(value) || typeof value === "string") {
        return value;
    }

    if (typeof value !== "object") {
        return null;
    }

    const record = value as {
        descopedSelections?: unknown;
        descoped_selections?: unknown;
        rocAutocompleteSaqTypes?: unknown;
        roc_autocomplete_saq_types?: unknown;
        selectedSaqTypes?: unknown;
        selected_saq_types?: unknown;
    };

    const candidates = [
        record.descopedSelections,
        record.descoped_selections,
        record.rocAutocompleteSaqTypes,
        record.roc_autocomplete_saq_types,
        record.selectedSaqTypes,
        record.selected_saq_types
    ];

    return candidates.find((candidate) => normalizeDescopedSelectionsSource(candidate).length > 0) ?? null;
}

function normalizeDescopedSelectionsSource(value: unknown): unknown[] {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value !== "string") {
        return [];
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return [];
    }

    const parsed = parseJsonValue(trimmed);
    if (parsed.ok && parsed.value !== trimmed) {
        return normalizeDescopedSelectionsSource(parsed.value);
    }

    return trimmed
        .split(",")
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
}

function parseJsonValue(value: string): { ok: boolean; value: unknown } {
    try {
        return { ok: true, value: JSON.parse(value) };
    } catch (error) {
        return { ok: false, value: null };
    }
}

function normalizeDescopedSelectionEntry(entry: unknown): string | null {
    const rawValue = resolveSelectionEntryValue(entry);
    if (typeof rawValue !== "string") {
        return null;
    }

    const trimmed = rawValue.trim();
    if (!trimmed) {
        return null;
    }

    const canonical = trimmed.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
    const labels: Record<string, string> = {
        "saq a": "SAQ A",
        "saq a ep": "SAQ A (EP)",
        "saq b": "SAQ B",
        "saq b ip": "SAQ B (IP)",
        "saq c": "SAQ C",
        "saq c vt": "SAQ C (VT)",
        "saq d": "SAQ D",
        "saq d merchant": "SAQ D",
        "saq d sp": "SAQ D (SP)",
        "saq d service provider": "SAQ D (SP)",
        "saq p2pe": "SAQ P2PE",
        "saq spoc": "SAQ SPoC"
    };

    return labels[canonical] ?? trimmed;
}

function resolveSelectionEntryValue(entry: unknown): unknown {
    if (!entry || typeof entry !== "object") {
        return entry;
    }

    const record = entry as {
        value?: unknown;
        label?: unknown;
        name?: unknown;
        formType?: unknown;
        form_type?: unknown;
    };

    return record.value ?? record.label ?? record.name ?? record.formType ?? record.form_type ?? null;
}
